// ============================================================================
// FSA MCA FILING SERVICE (ADR-0027)
// ============================================================================

import { get, post, put } from '../../client/axiosClient';
import { fsaMcaFilingEndpoints } from '../../endpoints/fsa/fsaMcaFiling.endpoints';
import type { ApiResponse, ID } from '../../types';
import type { McaSheet, SaveMcaValuesRequest } from '../../types/fsa/mcaFiling.types';

class FsaMcaFilingService {
  /** The filing set with every field's PRIMA value, source and typed value. */
  async getSheet(engagementId: ID, formCode: string): Promise<ApiResponse<McaSheet>> {
    const r = await get<ApiResponse<McaSheet>>(fsaMcaFilingEndpoints.sheet(engagementId, formCode));
    return r.data;
  }

  /** Save typed answers / overrides; 409 when someone else saved since the read. */
  async saveValues(engagementId: ID, formCode: string, request: SaveMcaValuesRequest): Promise<ApiResponse<McaSheet>> {
    const r = await put<ApiResponse<McaSheet>>(fsaMcaFilingEndpoints.values(engagementId, formCode), request);
    return r.data;
  }

  /**
   * Upload the portal's ZIP (or its .xlsx workbooks) and download them filled,
   * under their own names, with PRIMA-fill-report.txt.
   */
  async fill(engagementId: ID, formCode: string, files: File[]): Promise<void> {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    const r = await post<Blob>(fsaMcaFilingEndpoints.fill(engagementId, formCode), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
    const disposition = String(r.headers?.['content-disposition'] ?? '');
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match ? decodeURIComponent(match[1]) : `${formCode}-filled.zip`;
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const fsaMcaFilingService = new FsaMcaFilingService();
export default fsaMcaFilingService;
